import React, { useState, useEffect } from 'react';
import { Bed, Bath, Square, MapPin, Download, Info, Phone, Mail, MessageCircle, Pencil } from 'lucide-react';
import { Property, Agent } from '../types.ts';
import { getSupabase } from '../lib/supabase.ts';
import { Link } from 'react-router-dom';

interface PropertyCardProps {
  property: Property;
}

const PropertyCard: React.FC<PropertyCardProps> = ({ property }) => {
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [leadPurpose, setLeadPurpose] = useState<'details' | 'brochure' | 'contact'>('details');
  const [formData, setFormData] = useState({ name: '', email: '', phone: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
      const checkAdminStatus = () => {
        const session = localStorage.getItem('guardian_admin_session');
        setIsAdmin(!!session);
      };
      checkAdminStatus();
      window.addEventListener('guardian-login-state-changed', checkAdminStatus);
      return () => window.removeEventListener('guardian-login-state-changed', checkAdminStatus);
  }, []);

  const handleLeadSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    const supabase = getSupabase();
    if (supabase) { 
        await supabase.from('leads').insert({ 
          id: Date.now().toString(), 
          name: formData.name, 
          email: formData.email, 
          phone: formData.phone, 
          property_id: property.id 
        }); 
    }
    setTimeout(() => {
       setIsSubmitting(false); setSuccess(true);
       if (leadPurpose === 'brochure' && property.brochureUrl && property.type === 'off-plan') { window.open(property.brochureUrl, '_blank'); }
       setTimeout(() => { setShowLeadForm(false); setSuccess(false); setFormData({ name: '', email: '', phone: '' }); }, 2000);
    }, 1000);
  };

  const displayAgent: Agent | undefined = property.agent;
  const agentPhone = displayAgent?.phone || '+971505804669';
  const agentEmail = displayAgent?.email?.trim() || 'hello@guardianhousing.ae';
  const whatsappUrl = `https://wa.me/${(displayAgent?.whatsapp || '971505804669').replace(/[^0-9]/g, '')}?text=${encodeURIComponent(`Hi, I am interested in "${property.title}"`)}`;

  return (
    <>
    <div className="group bg-white rounded-sm overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 flex flex-col h-full relative font-sans">
      {isAdmin && (
          <Link 
            to={`/admin?editPropId=${property.id}`} 
            className="absolute top-3 left-3 z-40 bg-orange-600 text-white px-2 py-1 rounded shadow-lg hover:bg-orange-700 transition-all flex items-center gap-1.5" 
          >
              <Pencil size={12}/>
              <span className="text-[9px] font-bold uppercase tracking-widest">Edit</span>
          </Link>
      )}
      <Link to={`/property/${property.id}`} className="relative overflow-hidden h-48 md:h-56 block">
        <img src={property.imageUrl} alt={property.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"/>
        <div className="absolute top-2 right-2">
           <div className="bg-slate-900 text-white text-[8px] font-bold uppercase tracking-widest px-2 py-1 rounded-sm shadow-sm">{property.type.replace('-', ' ')}</div>
        </div>
      </Link>
      <div className="p-4 flex flex-col flex-grow">
        <div className="mb-1">
          <span className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">{property.propertyType}</span>
          <div className="text-lg font-bold text-slate-900 mt-1">
            {property.price.toLocaleString()} <span className="text-[10px] font-normal text-gray-500 uppercase">{property.currency}</span>
          </div>
        </div>
        <Link to={`/property/${property.id}`} className="text-sm font-serif text-slate-800 line-clamp-1 mb-1 group-hover:text-gold-600 transition-colors">{property.title}</Link>
        <div className="flex items-center text-gray-400 text-[10px] mb-4"><MapPin size={10} className="mr-1 shrink-0 text-gold-500" /> <span className="truncate">{property.location}</span></div>
        <div className="flex items-center gap-3 border-t border-gray-50 pt-3 mt-auto mb-4 text-slate-600 text-[11px] font-medium">
           <div className="flex items-center gap-1"><Bed size={14} className="text-gray-400"/> {property.beds}</div>
           <div className="flex items-center gap-1"><Bath size={14} className="text-gray-400"/> {property.baths}</div>
           <div className="flex items-center gap-1"><Square size={14} className="text-gray-400"/> {property.sqft} <span className="text-[9px]">Sqft</span></div>
        </div>
        <div className="grid grid-cols-3 gap-1">
           <a href={`tel:${agentPhone}`} className="flex items-center justify-center py-1.5 rounded bg-slate-50 text-slate-700 hover:bg-slate-900 hover:text-white transition-all text-[9px] font-bold uppercase tracking-widest border border-slate-100"><Phone size={10} className="mr-1" /> Call</a>
           <a href={`mailto:${agentEmail}`} className="flex items-center justify-center py-1.5 rounded bg-slate-50 text-slate-700 hover:bg-slate-900 hover:text-white transition-all text-[9px] font-bold uppercase tracking-widest border border-slate-100"><Mail size={10} className="mr-1" /> Email</a>
           <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center py-1.5 rounded bg-[#25D366] text-white hover:bg-[#128C7E] transition-all text-[9px] font-bold uppercase tracking-widest"><MessageCircle size={10} className="mr-1" /> WA</a>
        </div>
      </div>
    </div>
    {showLeadForm && (
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
        <div className="bg-white rounded-none shadow-2xl max-w-sm w-full p-8 relative border-t-4 border-gold-500 animate-fade-in">
          <button onClick={() => setShowLeadForm(false)} className="absolute top-4 right-4 text-gray-400">✕</button>
          <h3 className="text-xl font-serif text-slate-900 mb-6">{leadPurpose === 'brochure' ? 'Download Brochure' : 'Request Info'}</h3>
          {success ? <div className="text-center py-8 text-green-600 font-bold">✓ Request Sent</div> : (
             <form onSubmit={handleLeadSubmit} className="space-y-4">
                <input required type="text" placeholder="Name" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} className="w-full border p-3 rounded-sm text-xs outline-none focus:border-gold-500"/>
                <input required type="email" placeholder="Email" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} className="w-full border p-3 rounded-sm text-xs outline-none focus:border-gold-500"/>
                <input required type="tel" placeholder="Phone" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} className="w-full border p-3 rounded-sm text-xs outline-none focus:border-gold-500"/>
                <button disabled={isSubmitting} className="w-full bg-slate-900 text-white py-3 font-bold uppercase tracking-widest text-[10px] hover:bg-gold-500 transition-all">Submit Request</button>
             </form>
          )}
        </div>
      </div>
    )}
    </>
  );
};

export default PropertyCard;